import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, Mic, Brain, AudioLines } from "lucide-react";
import { owlService } from "../../Services";

const ChatInterface = ({
  messages,
  onSendMessage,
  loadMoreMessages,
  isLoadingMore,
  hasMoreMessages,
}) => {
  const [message, setMessage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const textareaRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [isNearTop, setIsNearTop] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const loadingRef = useRef(false);

  // Audio recording refs
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isRecordingRef = useRef(false);
  const TIME_INTERVAL = 10000; // 10 seconds per chunk

  // Scroll to bottom on new messages if we were near bottom
  useEffect(() => {
    if (chatContainerRef.current && isNearBottom) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isNearBottom]);

  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100;

    setIsNearTop(scrollTop < threshold);
    setIsNearBottom(scrollHeight - (scrollTop + clientHeight) < threshold);

    // Load more messages when scrolled to top
    if (scrollTop < threshold && hasMoreMessages && !loadingRef.current && !isLoadingMore) {
      loadingRef.current = true;
      loadMoreMessages().finally(() => {
        loadingRef.current = false;
      });
    }
  }, [hasMoreMessages, isLoadingMore, loadMoreMessages]);

  // Set up scroll listener
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const maxHeight = 24 * 10;
      textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + "px";
    }
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    autoResizeTextarea();
  };

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message.trim());
      setMessage("");
    }

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startRecording = async () => {
    console.log("start audio is called");

    try {
      console.log("🎤 Starting recording...");
      audioChunksRef.current = [];
      stopRecording();

      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;
      isRecordingRef.current = true;
      setIsRecording(true);

      // Setup media recorder
      const options = {
        audioBitsPerSecond: 128000,
        mimeType: MediaRecorder.isTypeSupported("audio/webm; codecs=opus")
          ? "audio/webm; codecs=opus"
          : "audio/webm",
      };

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: options.mimeType });
          console.log("Audio blob ready for upload:", audioBlob);
          await transcribeAudio(audioBlob);
          audioChunksRef.current = [];
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("Recorder error:", event.error);
        stopRecording();
      };

      // Start recording in chunks
      mediaRecorder.start();

      // Set up periodic data available triggers
      const chunkInterval = setInterval(() => {
        if (mediaRecorder.state === "recording") {
          mediaRecorder.requestData();
        }
      }, TIME_INTERVAL);

      return () => clearInterval(chunkInterval);
    } catch (err) {
      console.error("Microphone access failed:", err);
      stopRecording();
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = async () => {
    if (!isRecordingRef.current) return;

    isRecordingRef.current = false;
    setIsRecording(false);

    // Stop media recorder if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    // Stop all tracks in the stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    mediaRecorderRef.current = null;
    console.log("✅ Recording stopped");
  };

  // Transcribe audio using the API
  const transcribeAudio = async (blob) => {
    if (!blob) return;

    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      formData.append("language", "en");

      const response = await owlService.transcribeAudio(formData);
      if (response.success && response.data.transcription) {
        setMessage(prev => {
          const newTranscription = response.data.transcription.trim();
          
          if (prev.trim()) {
            return prev + " " + newTranscription;
          } else {
            return newTranscription;
          }
        });
        
        // Auto-resize textarea after transcription
        setTimeout(() => {
          autoResizeTextarea();
        }, 0);
      } else {
        throw new Error(response.message || "Transcription failed");
      }
    } catch (error) {
      console.error("Transcription error:", error);
      // setMessage((prev) => {
      //   const errorMsg = "[Transcription error: " + error.message + "]";
      //   if (prev.trim()) {
      //     return prev + "\n" + errorMsg;
      //   } else {
      //     return errorMsg;
      //   }
      // });
      
      // Auto-resize textarea after error message
      setTimeout(() => {
        autoResizeTextarea();
      }, 0);
    } finally {
      setIsTranscribing(false);
    }
  };

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col bg-background h-full">
      {/* Chat Messages Area */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto"
        style={{ scrollBehavior: "smooth" }}
      >
        {isLoadingMore && (
          <div className="flex justify-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {messages.length === 0 && !isLoadingMore ? (
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center mx-auto">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <p className="text-muted-foreground">How can I help you today?</p>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {messages.map((message, i) => (
              <div
                key={`${message.id || i}-${message.timestamp || Date.now()}`}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`rounded-lg px-4 py-2 max-w-[80%] ${
                    message.role === "user"
                      ? "bg-green-100 text-gray-800"
                      : message.isLoading
                      ? "bg-gray-200 animate-pulse"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                </div>
              </div>
            ))}
            {/* Empty div for scroll anchoring */}
            <div style={{ float: "left", clear: "both" }} />
          </div>
        )}
      </div>
      <div className="p-6 border-t border-border bg-background">
        <div className="max-w-4xl mx-auto">
          {/* Transcribing Indicator */}
          {isTranscribing && (
            <div className="mb-3 flex items-center justify-center">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full shadow-lg animate-pulse">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Transcribing audio...</span>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-3 bg-input border border-border focus-within:ring-2 focus-within:ring-primary rounded-xl px-4 py-2">
            {/* Text Input */}
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Message"
              className="flex-1 resize-none bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground leading-[1.5rem] max-h-[240px]"
              rows={1}
            />

            {/* Mic Button */}
            <div>{isRecording}</div>
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              className={`p-2 rounded-full transition-colors cursor-pointer ${
                isRecording ? "bg-red-500" : "bg-[#044433] hover:bg-[#102620]/90"
              }`}
              disabled={!navigator.mediaDevices?.getUserMedia}
              aria-label={isRecording ? "Stop recording" : "Start recording"}
            >
              {isRecording ? (
                <div className="relative h-[20px] w-[20px]">
                  <AudioLines className="w-4 h-4 text-white z-50 absolute" />
                  <span className="flex h-10 w-10 absolute top-[-11px] left-[-12px]">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-10 w-10 bg-red-500 shrink-0"></span>
                  </span>
                </div>
              ) : (
                <Mic className="w-4 h-4 text-white" />
              )}
            </button>

            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={!message.trim()}
              className="p-2 bg-[#044433] hover:bg-[#102620]/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors text-primary-foreground"
            >
              <Send className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
